import { useEffect, useState } from 'react';
import { Modal } from 'antd';
import {
  IFarmResource,
  ILineGroup,
  ILineResource,
} from '../../entities/farms.entities';
import { Button, CloseIcon, Dropdown, Input, Subtitle } from '../shared';
import ModalFeedbackView from '../shared/feedback/ModalFeedbackView';
import { sendSingleRequest } from '../../apis';
import { useDispatch, useSelector } from 'react-redux';
import { showFeedback } from '../../store/ui/ui.actions';
import { loadFarmsData } from '../../store/farms/farms.actions';
import { selectFarmingMethods } from '../../store/utils/utils.selector';
import LineLocationModal from '../shared/modal/LineLocationModal';
import { selectLang } from '../../store/ui/ui.selector';
import { selectAccount } from '../../store/auth/auth.selector';
import { getUnitOfSpecie } from '../../lib/common.helpers';
import { translate, transUnit } from '../../lib/lang.helper';
import { selectInventories } from '../../store/inventories/inventories.selector';
import SectionLocationForm from '../shared/modal/SectionLocationForm';
import { numToStr } from '../../lib/common.helpers';
import { TLineType } from '../../entities/general.entities';

interface IFormData {
  line_type: TLineType;
  line_name: string;
  backbone?: number | null;
  width?: number | null;
  length?: number | null;
  points: ILineResource['points'];
  line_group_id: number | null;
  basket_count?: number | null;
  tank_area?: number | null;
  farming_method?: string;
  inventory_id?: number | null;
}

const defaultForm: IFormData = {
  line_type: 'LONGLINE',
  line_name: '',
  points: null,
  line_group_id: null,
};

interface Props {
  visible: boolean;
  lineData: ILineResource;
  onClose: (d?: any) => void;
  farmData: IFarmResource;
}

const LineFormModal = ({ visible, lineData, onClose, farmData }: Props) => {
  const dispatch = useDispatch<any>();
  const farmingMethods = useSelector(selectFarmingMethods);
  const lang = useSelector(selectLang);
  const inventories = useSelector(selectInventories);
  const unitType = useSelector(selectAccount)?.display_unit;

  const [groups, setGroups] = useState<ILineGroup[]>([]);
  const [disabled, setDisabled] = useState(false);
  const [form, setForm] = useState<IFormData>(() => {
    return {
      ...defaultForm,
      line_type: lineData.line_type,
      line_name: lineData.line_name,
      backbone: lineData.backbone,
      width: lineData.width,
      length: lineData.length,
      points: lineData.points ?? null,
      line_group_id: lineData.line_group?.id ?? defaultForm.line_group_id,
      basket_count: lineData.basket_count ?? null,
      tank_area: lineData.tank_area,
      farming_method: lineData.farming_method ?? undefined,
      inventory_id: lineData.inventory_id,
    };
  });
  const [visibleMap, setVisibleMap] = useState(false);

  const showError = (message: string) =>
    dispatch(
      showFeedback({
        isMessageModal: true,
        message: translate(lang, message),
        type: 'error',
      }),
    );

  const changeLineType = (type: TLineType) => {
    let tmp = { ...form, line_type: type };
    if (type === 'LONGLINE' || type === 'DROPPERS') {
      tmp.width = tmp.length = undefined;
    } else {
      tmp.backbone = undefined;
    }
    setForm(tmp);
  };
  const updateForm = (key: keyof IFormData, val: any) => {
    if (
      ['backbone', 'width', 'length', 'basket_count', 'tank_area'].includes(key)
    ) {
      val = val.length <= 0 ? null : Number(val);
    }
    setForm({ ...form, [key]: val });
  };

  const handleChangePoints = (i: number, x: 'lat' | 'lng', v: string) => {
    let points: any = form.points ? [...form.points] : [];
    while (points.length <= i) points.push({ lat: null, lng: null });
    points[i][x] = Number(v);
    setForm(prv => ({ ...prv, points }));
  };

  const confirmClick = async () => {
    if (form.line_name.length <= 0) {
      showError('_enter_valid_line_name');
      return;
    }
    if (lineData.crop_type === 'MUSSEL') {
      if (form.line_type === 'PLATFORM') {
        if (!form.width) {
          showError('Please input valid capacity width');
          return;
        }
        if (!form.length) {
          showError('Please input valid capacity length');
          return;
        }
      } else {
        if (!form.backbone) {
          showError(
            translate(
              lang,
              'Please input valid %s',
              transUnit(lang, 'backbone', farmData.structure),
            ),
          );
          return;
        }
        if (farmData.structure === 'RAFT' && !form.basket_count) {
          showError(
            translate(
              lang,
              'Please input valid %s',
              transUnit(lang, 'basket_count', farmData.structure),
            ),
          );
          return;
        }
      }
    } else if (lineData.crop_type === 'OYSTER') {
      if (!form.backbone) {
        showError('_enter_valid_line_length');
        return;
      }
      if (!form.farming_method) {
        showError('_sel_farming_method');
        return;
      }
      if (!form.basket_count) {
        showError('_enter_valid_num_baskets');
        return;
      }
    } else {
      if (!form.tank_area) {
        showError('_enter_valid_tank_area');
        return;
      }
    }
    setDisabled(true);
    const response = await sendSingleRequest(
      { line_id: lineData.id, ...form },
      'PUT',
      `api/farm/line/lines/${lineData.id}`,
      true,
    );
    if (response.status) {
      await dispatch(loadFarmsData());
      setDisabled(false);
      dispatch(
        showFeedback({
          isMessage: true,
          message: translate(lang, '_line_updated_ok'),
          type: 'success',
        }),
      );
      onClose(true);
    } else {
      setDisabled(false);
      showError(response.data?.message ?? 'Failed to update the line');
    }
  };

  useEffect(() => {
    sendSingleRequest(
      { farm_id: lineData.farm_id },
      'GET',
      'api/line-group',
      true,
    ).then(res => {
      if (res.status) {
        setGroups(res.data);
      } else {
        showError(res.data?.message ?? 'Failed to load line groups');
      }
    });
  }, [lineData]);

  const groupsOption = [
    { id: '-1', value: '', label: '(None)' },
    ...groups.map(x => ({
      id: x.id.toString(),
      value: x.id.toString(),
      label: x.name,
    })),
  ];

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      closable
      closeIcon={<CloseIcon />}
      footer={null}
      width={650}
    >
      <div className='wrap'>
        <div className='d-flex align-center mb-16'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {translate(
              lang,
              farmData.structure === 'RAFT'
                ? 'Edit section details'
                : 'Edit line details',
            )}
          </Subtitle>
        </div>
        <div className='mt-17 mb-17'>
          <Input
            label={transUnit(lang, 'line_name', farmData.structure)}
            type='text'
            value={form.line_name}
            onChange={e => updateForm('line_name', e.target.value)}
          />
        </div>
        {lineData.crop_type === 'MUSSEL' ? (
          <>
            <div className='mb-17'>
              <Dropdown
                label={transUnit(lang, 'line_type', farmData.structure)}
                options={[
                  { id: 'L', value: 'LONGLINE', label: 'Longline' },
                  { id: 'D', value: 'DROPPERS', label: 'Droppers' },
                  { id: 'P', value: 'PLATFORM', label: 'Platform' },
                ]}
                value={form.line_type}
                onChange={v => changeLineType(v as any)}
                disabled={farmData.structure === 'RAFT'}
              />
            </div>
            {form.line_type === 'PLATFORM' ? (
              <div className='d-flex mb-17'>
                <div className='w-100 mr-12'>
                  <Input
                    label={translate(lang, 'Capacity width')}
                    type='number'
                    value={numToStr(form.width)}
                    onChange={e => updateForm('width', e.target.value)}
                    unit={getUnitOfSpecie(unitType, 'distance_m')}
                  />
                </div>
                <div className='w-100 ml-12'>
                  <Input
                    label={translate(lang, 'Capacity length')}
                    type='number'
                    value={numToStr(form.length)}
                    onChange={e => updateForm('length', e.target.value)}
                    unit={getUnitOfSpecie(unitType, 'distance_m')}
                  />
                </div>
              </div>
            ) : (
              <div className='mb-17'>
                <Input
                  label={transUnit(lang, 'backbone', farmData.structure)}
                  unit={getUnitOfSpecie(unitType, 'distance_m')}
                  type='number'
                  value={numToStr(form.backbone)}
                  onChange={e => updateForm('backbone', e.target.value)}
                />
              </div>
            )}
            {farmData.structure === 'RAFT' && (
              <div className='mb-17'>
                <Input
                  label={transUnit(lang, 'basket_count', farmData.structure)}
                  type='number'
                  value={numToStr(form.basket_count)}
                  onChange={e => updateForm('basket_count', e.target.value)}
                />
              </div>
            )}
          </>
        ) : lineData.crop_type === 'OYSTER' ? (
          <>
            <div className='mb-17'>
              <Input
                type='number'
                label={translate(lang, 'Length')}
                unit='m'
                value={numToStr(form.backbone)}
                onChange={e => updateForm('backbone', e.target.value)}
              />
            </div>
            <div className='mb-17'>
              <Dropdown
                label={translate(lang, 'Farming method')}
                options={farmingMethods.map(x => ({
                  id: x.name,
                  value: x.name,
                  label: x.name,
                }))}
                value={form.farming_method}
                onChange={v => updateForm('farming_method', v)}
              />
            </div>
            {inventories.length > 0 && (
              <div className='mb-17'>
                <Dropdown
                  label={translate(lang, 'Default inventory')}
                  options={inventories.map(x => ({
                    id: x.id.toString(),
                    value: x.id.toString(),
                    label: `${x.type} ${x.name}`,
                  }))}
                  value={form.inventory_id?.toString()}
                  onChange={v => updateForm('inventory_id', Number(v))}
                />
              </div>
            )}
            <div className='mb-17'>
              <Input
                label={translate(lang, 'Baskets capacity')}
                type='number'
                value={numToStr(form.basket_count)}
                onChange={e => updateForm('basket_count', e.target.value)}
              />
            </div>
          </>
        ) : (
          <div className='mb-17'>
            <Input
              type='number'
              label={translate(lang, 'Tank area')}
              unit={getUnitOfSpecie(unitType, 'area_sqm')}
              value={numToStr(form.tank_area)}
              onChange={e => updateForm('tank_area', e.target.value)}
            />
          </div>
        )}
        {farmData.structure === 'RAFT' ? (
          <SectionLocationForm
            className='mb-17'
            points={form.points ?? []}
            onChange={pts => updateForm('points', pts)}
            farmData={farmData}
          />
        ) : (
          <div className='mb-17'>
            {[0, 1].map(x => (
              <div key={x} className='pb-17 w-100 d-flex justify-between'>
                <div className='mr-17'>
                  <Input
                    label={translate(
                      lang,
                      `${x === 0 ? 'Start' : 'End'} location Latitude`,
                    )}
                    placeholder={translate(lang, 'Latitude')}
                    type='number'
                    value={
                      form.points && form.points.length > x
                        ? numToStr(form.points[x].lat)
                        : ''
                    }
                    onChange={e => handleChangePoints(x, 'lat', e.target.value)}
                  />
                </div>
                <div className='ml-17 mr-17'>
                  <Input
                    label={translate(lang, 'Longitude')}
                    placeholder={translate(lang, 'Longitude')}
                    className='mr-17'
                    type='number'
                    value={
                      form.points && form.points.length > x
                        ? numToStr(form.points[x].lng)
                        : ''
                    }
                    onChange={e => handleChangePoints(x, 'lng', e.target.value)}
                  />
                </div>
                <div className='ml-17 d-flex align-end'>
                  <Button
                    width='wide'
                    size={1}
                    type='fill'
                    color='blue'
                    onClick={() => setVisibleMap(true)}
                  >
                    {translate(lang, 'Pickup')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className='mb-24'>
          <Dropdown
            label={transUnit(lang, 'line_group', farmData?.structure)}
            value={numToStr(form.line_group_id)}
            options={groupsOption}
            onChange={v =>
              updateForm('line_group_id', v.length <= 0 ? null : Number(v))
            }
          />
        </div>
      </div>
      {visibleMap && (
        <LineLocationModal
          visible={true}
          farmID={lineData.farm_id}
          points={form.points ?? undefined}
          onConfirm={points => {
            updateForm('points', points);
            setVisibleMap(false);
          }}
          onCancel={() => setVisibleMap(false)}
          lineData={lineData}
        />
      )}
      <div className='modal-button d-flex justify-end align-center mt-32'>
        <Button
          width='small'
          size={2}
          type='fill'
          color='red'
          className='rsp-btn'
          onClick={onClose}
          disabled={disabled}
        >
          {translate(lang, 'Close')}
        </Button>
        <Button
          width='small'
          size={2}
          type='fill'
          color='green'
          className='rsp-btn ml-16'
          onClick={confirmClick}
          disabled={disabled}
        >
          {translate(lang, 'Confirm')}
        </Button>
      </div>
      <ModalFeedbackView />
    </Modal>
  );
};

export default LineFormModal;
